import { memo } from 'react';
import { EdgeProps, getBezierPath, EdgeLabelRenderer } from 'reactflow';

interface AnimatedEdgeData {
  callsPerMinute?: number;
  avgLatency?: number;
  errorRate?: number;
  protocol?: string;
  isHighlighted?: boolean;
  isDimmed?: boolean;
}

function AnimatedEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  style = {},
  markerEnd,
}: EdgeProps<AnimatedEdgeData>) {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const callsPerMinute = data?.callsPerMinute || 0;
  const avgLatency = data?.avgLatency || 0;
  const errorRate = data?.errorRate || 0;
  const isHighlighted = data?.isHighlighted || false;
  const isDimmed = data?.isDimmed || false;

  // Determine edge color based on error rate
  const getEdgeColor = () => {
    if (errorRate > 10) return '#ef4444'; // red
    if (errorRate > 5) return '#f59e0b'; // amber
    return '#6366f1'; // indigo
  };

  // Determine stroke width based on traffic
  const getStrokeWidth = () => {
    if (callsPerMinute > 1000) return 4;
    if (callsPerMinute > 100) return 3;
    return 2;
  };

  const edgeColor = getEdgeColor();
  const strokeWidth = getStrokeWidth();

  // Animation speed based on traffic volume
  const animationDuration = callsPerMinute > 100 ? '1s' : callsPerMinute > 10 ? '2s' : '3s';

  return (
    <>
      {/* Glow effect for highlighted edges */}
      {isHighlighted && (
        <path
          d={edgePath}
          fill="none"
          stroke={edgeColor}
          strokeWidth={strokeWidth + 6}
          strokeOpacity={0.2}
          filter="blur(4px)"
        />
      )}

      {/* Background path */}
      <path
        id={`${id}-bg`}
        d={edgePath}
        fill="none"
        stroke={edgeColor}
        strokeWidth={strokeWidth}
        strokeOpacity={isDimmed ? 0.1 : 0.2}
        className="transition-all duration-300"
      />

      {/* Animated path */}
      <path
        id={id}
        d={edgePath}
        fill="none"
        stroke={edgeColor}
        strokeWidth={strokeWidth}
        strokeOpacity={isDimmed ? 0.2 : 1}
        strokeLinecap="round"
        strokeDasharray="8 4"
        className="transition-all duration-300"
        style={{
          ...style,
          animation: `flowAnimation ${animationDuration} linear infinite`,
        }}
        markerEnd={markerEnd}
      />

      {/* Particle effect */}
      <circle r={3} fill={edgeColor} opacity={isDimmed ? 0.2 : 0.8}>
        <animateMotion
          dur={animationDuration}
          repeatCount="indefinite"
          path={edgePath}
        />
      </circle>

      {/* Edge label */}
      <EdgeLabelRenderer>
        <div
          className={`
            absolute pointer-events-auto nodrag nopan
            transform -translate-x-1/2 -translate-y-1/2
            transition-all duration-300
            ${isDimmed ? 'opacity-20' : 'opacity-100'}
          `}
          style={{
            left: labelX,
            top: labelY,
          }}
        >
          <div className={`
            flex items-center gap-1.5 px-2 py-1 rounded-lg
            bg-white/95 dark:bg-gray-800/95
            border border-gray-200 dark:border-gray-700
            shadow-lg backdrop-blur-sm
            text-xs font-medium
            ${isHighlighted ? 'ring-2 ring-indigo-500' : ''}
          `}>
            {/* Protocol badge */}
            {data?.protocol && (
              <span className={`
                px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase
                ${data.protocol === 'http' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                  data.protocol === 'grpc' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                  data.protocol === 'database' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                  'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'}
              `}>
                {data.protocol}
              </span>
            )}

            {/* Calls per minute */}
            <span className="text-gray-600 dark:text-gray-300 flex items-center gap-0.5">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              {callsPerMinute > 1000 ? `${(callsPerMinute / 1000).toFixed(1)}k` : callsPerMinute}/m
            </span>

            {/* Latency */}
            <span className={`flex items-center gap-0.5 ${
              avgLatency > 500 ? 'text-red-600 dark:text-red-400' :
              avgLatency > 200 ? 'text-amber-600 dark:text-amber-400' :
              'text-gray-500 dark:text-gray-400'
            }`}>
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {avgLatency.toFixed(0)}ms
            </span>

            {/* Error indicator */}
            {errorRate > 0 && (
              <span className={`flex items-center gap-0.5 ${
                errorRate > 5 ? 'text-red-600 dark:text-red-400' :
                'text-amber-600 dark:text-amber-400'
              }`}>
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                {errorRate.toFixed(1)}%
              </span>
            )}
          </div>
        </div>
      </EdgeLabelRenderer>

      {/* CSS Keyframes */}
      <style>
        {`
          @keyframes flowAnimation {
            0% {
              stroke-dashoffset: 24;
            }
            100% {
              stroke-dashoffset: 0;
            }
          }
        `}
      </style>
    </>
  );
}

export default memo(AnimatedEdge);
